// src/pages/services/CloudComputing.jsx - Premium Cloud Services
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Cloud, Server, Database, Shield, Zap, Globe, Layers, BarChart3 } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function CloudComputing() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Cloud className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Cloud Migration',
      description: 'Seamless migration of your infrastructure to the cloud',
      features: ['Assessment & Planning', 'Data Migration', 'Application Migration', 'Zero Downtime']
    },
    {
      icon: <Server className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Infrastructure as Code',
      description: 'Automated infrastructure provisioning and management',
      features: ['Terraform', 'CloudFormation', 'Ansible', 'CI/CD Integration']
    },
    {
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Cloud Architecture',
      description: 'Design scalable, resilient cloud architectures',
      features: ['High Availability', 'Auto Scaling', 'Load Balancing', 'Disaster Recovery']
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Cloud Security',
      description: 'Comprehensive cloud security and compliance',
      features: ['IAM Configuration', 'Encryption', 'Security Audits', 'Compliance']
    }
  ];

  const cloudProviders = [
    { name: 'Amazon AWS', icon: '☁️', color: 'from-orange-500 to-yellow-500', services: ['EC2', 'S3', 'Lambda', 'RDS', 'EKS'] },
    { name: 'Microsoft Azure', icon: '🔷', color: 'from-blue-500 to-cyan-500', services: ['VMs', 'Blob Storage', 'Functions', 'AKS', 'SQL'] },
    { name: 'Google Cloud', icon: '🌐', color: 'from-red-500 to-yellow-500', services: ['Compute Engine', 'Cloud Storage', 'GKE', 'BigQuery'] },
    { name: 'DigitalOcean', icon: '🌊', color: 'from-blue-600 to-cyan-600', services: ['Droplets', 'Spaces', 'Kubernetes', 'Databases'] }
  ];

  const benefits = [
    { title: 'Cost Savings', value: 'Up to 60%', description: 'Reduce infrastructure costs' },
    { title: 'Scalability', value: 'Auto-Scale', description: 'Scale on demand' },
    { title: 'Uptime', value: '99.99%', description: 'High availability' },
    { title: 'Speed', value: '10x Faster', description: 'Deployment speed' }
  ];

  const pricingPlans = [
    {
      name: 'Cloud Consultation',
      price: 9999,
      monthlySupport: 499,
      color: 'cyan',
      badge: null,
      features: [
        'Infrastructure Assessment',
        'Cloud Architecture Design',
        'Migration Strategy & Roadmap',
        '2 Months Support Included',
        'Cost Optimization Plan',
        'Security Best Practices'
      ]
    },
    {
      name: 'Cloud Migration',
      price: 34999,
      monthlySupport: 1499,
      color: 'blue',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Complete Cloud Migration',
        'Infrastructure as Code Setup',
        'Database & Application Migration',
        '4 Months Support Included',
        'Performance Optimization',
        'CI/CD Pipeline Setup',
        'Disaster Recovery Config'
      ]
    },
    {
      name: 'Managed Services',
      price: null,
      monthlySupport: 3999,
      color: 'indigo',
      badge: null,
      features: [
        '24/7 Cloud Monitoring',
        'Dedicated DevOps Team',
        'Incident Response & Resolution',
        'Cost Optimization Reviews',
        'Security & Compliance',
        'Auto-Scaling Management',
        'Monthly Performance Reports'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-cyan-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Cloud Computing Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-cyan-100 mb-6 sm:mb-8 leading-relaxed">
                Transform your business with scalable, secure, and cost-effective cloud solutions. Expert cloud architecture, migration, and managed services.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Cloud Journey
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Cloud Projects
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop"
                alt="Cloud Computing"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group hover:scale-105 transition-transform p-2 sm:p-4">
                <div className="text-2xl sm:text-4xl font-black text-cyan-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{benefit.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{benefit.title}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Cloud Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive cloud solutions from strategy to implementation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center text-cyan-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Providers */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Cloud Platforms We Work With
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Expertise across all major cloud providers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {cloudProviders.map((provider, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition border border-gray-200/50 dark:border-gray-700/50">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${provider.color} rounded-lg flex items-center justify-center text-3xl sm:text-4xl text-white mb-3 sm:mb-4 mx-auto`}>
                  {provider.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4 text-gray-900 dark:text-white">{provider.name}</h3>
                <div className="space-y-2">
                  {provider.services.map((service, idx) => (
                    <div key={idx} className="bg-gray-100 dark:bg-gray-800 px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-center text-gray-700 dark:text-gray-300 font-medium">
                      {service}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Cloud Migration Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Assessment', desc: 'Analyze infrastructure' },
              { step: '2', title: 'Planning', desc: 'Design cloud architecture' },
              { step: '3', title: 'Migration', desc: 'Execute migration plan' },
              { step: '4', title: 'Optimization', desc: 'Optimize performance' },
              { step: '5', title: 'Management', desc: 'Ongoing monitoring' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-cyan-600 to-blue-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Cloud Service Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for cloud transformation. From consultation to managed services.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                cyan: { bg: 'from-cyan-600 to-blue-600', light: 'from-cyan-50 to-blue-50', hover: 'hover:border-cyan-200' },
                blue: { bg: 'from-blue-600 to-indigo-600', light: 'from-blue-50 to-indigo-50', hover: 'hover:border-blue-200' },
                indigo: { bg: 'from-indigo-600 to-purple-600', light: 'from-indigo-50 to-purple-50', hover: 'hover:border-indigo-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-blue-500 dark:border-blue-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 dark:from-blue-600/20 dark:to-indigo-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Cloud Consultation' && 'Perfect to start your journey'}
                    {plan.name === 'Cloud Migration' && 'Best for full migration'}
                    {plan.name === 'Managed Services' && 'Ongoing cloud operations'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'cyan' ? '#0891b2, #0284c7' : plan.color === 'blue' ? '#0284c7, #4f46e5' : '#4f46e5, #7c3aed'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'cyan' ? '#0891b2, #0284c7' : plan.color === 'blue' ? '#0284c7, #4f46e5' : '#4f46e5, #7c3aed'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'cyan' ? '#0891b2, #0284c7' : plan.color === 'blue' ? '#0284c7, #4f46e5' : '#4f46e5, #7c3aed'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'cyan' ? '#cffafe' : plan.color === 'blue' ? '#dbeafe' : '#e0e7ff'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'cyan' ? '#0891b2' : plan.color === 'blue' ? '#0284c7' : '#4f46e5'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Managed Services' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🔒', text: 'Security Best Practices' },
                { icon: '📊', text: 'Performance Monitoring' },
                { icon: '💰', text: 'Cost Optimization' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-2xl border border-cyan-200/50 dark:border-cyan-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Cloud Migration FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does cloud migration take?',
                  a: 'Simple migrations: 2-4 weeks. Complex enterprise migrations: 3-6 months. We provide detailed timelines based on your infrastructure size and complexity during the assessment phase.'
                },
                {
                  q: 'Will there be downtime during migration?',
                  a: 'We implement zero-downtime migration strategies using techniques like blue-green deployments, rolling updates, and parallel running. Critical systems stay operational throughout the process.'
                },
                {
                  q: 'Which cloud provider should I choose?',
                  a: 'We recommend based on your needs: AWS for comprehensive services, Azure for Microsoft integration, Google Cloud for data analytics, DigitalOcean for simplicity. We\'ll help you decide.'
                },
                {
                  q: 'What about data security and compliance?',
                  a: 'We implement enterprise-grade security: encryption at rest and in transit, IAM policies, VPC configurations, compliance with SOC2, HIPAA, GDPR standards, and regular security audits.'
                },
                {
                  q: 'How much can I save with cloud migration?',
                  a: 'Typical savings: 30-60% on infrastructure costs through rightsizing, reserved instances, auto-scaling, and eliminating on-premise hardware maintenance. We provide detailed ROI analysis.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Cloud className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Move to the Cloud?
          </h2>
          <p className="text-base sm:text-xl text-cyan-100 mb-6 sm:mb-8 px-2">
            Transform your infrastructure with scalable, secure cloud solutions. Let's build your cloud success story together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-cyan-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>☁️ Schedule Cloud Assessment</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="cloud-computing" />
      </div>
    </div>
  );
}
